/**
 * Vera Admin — user role assignment management.
 *
 * Gated by the global `Admin` Entra role. Two modes:
 *   - By User: pick a user, toggle their roles. Select/clear all their roles.
 *   - By Role: pick a role, toggle which users have it. Bulk grant/revoke
 *     across all currently-visible (filtered) users.
 *
 * All grants/revokes flow through the @shared/entities/user-role-assignment
 * hooks. Role changes reach signed-in users within 15 minutes via the BFF's
 * rolesRefreshMiddleware, or immediately at next sign-in (BFF
 * reads from Dataverse at login and stores in session).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Combobox,
  Divider,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  OptionGroup,
  Spinner,
  Subtitle2,
  Tab,
  TabList,
  Textarea,
  Title2,
  makeStyles,
  mergeClasses,
  tokens,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import {
  CheckmarkSquareRegular,
  ClipboardPasteRegular,
  DismissSquareRegular,
  PersonAddRegular,
  SearchRegular,
  ShieldKeyholeRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { dataverseClient, escapeODataString, sanitizeUserSearchQuery } from '@shared/api';
import { veracityColors } from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import { GLOBAL_ADMIN_ROLE, ROLE_PREFIX, VERA_APP_ROLES, capabilitiesForRole, groupedVeraRoles, hasGlobalAdmin } from '@shared/auth';
import {
  useUserRoleAssignments,
  userRoleAssignmentKeys,
  grantRoleWithTeamSync,
  revokeRoleWithTeamSync,
  userRoleAssignmentApi,
} from '@shared/entities/user-role-assignment';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '@shared/config/env';
import { AccessHealthView } from './AccessHealthView';

// =============================================================================
// Data helpers
// =============================================================================

interface FoundUser {
  oid: string;
  homeAccountId: string;
  systemuserid: string;
  name: string;
  email: string;
  /** Pre-provision target: no systemuser row yet. Roles are granted as
   *  pending (email-keyed) rows the BFF reconciles on first sign-in. */
  pending?: boolean;
}

interface SystemUserRow {
  systemuserid: string;
  fullname: string;
  internalemailaddress: string;
  azureactivedirectoryobjectid: string;
}

// Use the shared `escapeODataString` + `sanitizeUserSearchQuery` from
// @shared/api below. Local helpers were inlined originally; consolidated
// to the shared utils so AppShell + AdminPage stay in lockstep.

function toFoundUser(row: SystemUserRow, tenantId: string): FoundUser {
  return {
    oid: row.azureactivedirectoryobjectid,
    homeAccountId: `${row.azureactivedirectoryobjectid}.${tenantId}`,
    systemuserid: row.systemuserid,
    name: row.fullname || 'Unknown',
    email: row.internalemailaddress || '',
  };
}

async function searchSystemUsers(query: string, tenantId: string): Promise<FoundUser[]> {
  const safe = sanitizeUserSearchQuery(query);
  if (safe.length < 2) return [];
  const result = await dataverseClient.list<SystemUserRow & { isdisabled: boolean }>('systemusers', {
    filter: `(contains(fullname,'${escapeODataString(safe)}') or contains(internalemailaddress,'${escapeODataString(safe)}')) and isdisabled eq false and azureactivedirectoryobjectid ne null`,
    select: ['systemuserid', 'fullname', 'internalemailaddress', 'azureactivedirectoryobjectid'],
    top: 20,
    orderBy: 'fullname asc',
  });
  return result.value.filter((u) => u.azureactivedirectoryobjectid).map((r) => toFoundUser(r, tenantId));
}

/** Stateless email-shape check (avoids the `g`-flag lastIndex trap on EMAIL_REGEX). */
function looksLikeEmail(s: string): boolean {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(s.trim());
}

/** Synthetic selection for a person who has never signed in: grants become
 *  pending rows keyed by email (ADO #1467, no Dynamics-link visit needed). */
function pendingFoundUser(email: string): FoundUser {
  const normalized = email.trim().toLowerCase();
  return { oid: '', homeAccountId: '', systemuserid: '', name: normalized, email: normalized, pending: true };
}

async function fetchAllActiveSystemUsers(tenantId: string): Promise<FoundUser[]> {
  // listAll follows @odata.nextLink so we don't silently truncate at 500 once
  // the tenant grows. Dataverse pages at 5000 internally; the bulk-paste +
  // by-role admin views need every active user to do their email lookups.
  const rows = await dataverseClient.listAll<SystemUserRow & { isdisabled: boolean }>(
    'systemusers',
    {
      filter: `isdisabled eq false and azureactivedirectoryobjectid ne null`,
      select: ['systemuserid', 'fullname', 'internalemailaddress', 'azureactivedirectoryobjectid'],
      orderBy: 'fullname asc',
    },
  );
  return rows.filter((u) => u.azureactivedirectoryobjectid).map((r) => toFoundUser(r, tenantId));
}

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
  },
  titleIcon: {
    fontSize: '28px',
    color: veracityColors.veracityOrange,
  },
  subtitle: {
    color: veracityColors.duskBlue,
    marginBottom: tokens.spacingVerticalXL,
  },
  tabList: {
    marginBottom: tokens.spacingVerticalL,
  },
  searchBox: {
    width: '100%',
    maxWidth: '480px',
    marginBottom: tokens.spacingVerticalM,
  },
  resultsList: {
    maxWidth: '480px',
    marginBottom: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    maxHeight: '280px',
    overflowY: 'auto',
  },
  resultRow: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    ':hover': { backgroundColor: veracityColors.firstLight },
  },
  resultRowActive: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `2px solid ${veracityColors.veracityOrange}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: veracityColors.firstLight,
  },
  resultName: { color: veracityColors.midnightCharcoal, fontWeight: tokens.fontWeightSemibold },
  resultEmail: { color: veracityColors.duskBlue, fontSize: tokens.fontSizeBase200 },
  selectedPanel: {
    padding: tokens.spacingVerticalL,
    backgroundColor: '#FFFFFF',
  },
  selectedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
  },
  bulkActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  roleGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  roleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    ':hover': { backgroundColor: veracityColors.firstLight },
  },
  roleText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  roleValue: {
    color: veracityColors.midnightCharcoal,
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
  },
  roleDescription: {
    color: veracityColors.duskBlue,
  },
  userTable: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '520px',
    overflowY: 'auto',
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#FFFFFF',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${veracityColors.firstLight}`,
    ':last-child': { borderBottom: 'none' },
  },
  userText: { flex: 1 },
  pickRolePrompt: {
    padding: tokens.spacingVerticalXXL,
    color: veracityColors.duskBlue,
    textAlign: 'center',
  },
  // ---- By Role: two-pane master-detail ----
  byRoleLayout: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    alignItems: 'flex-start',
  },
  roleSidebar: {
    width: '300px',
    flexShrink: 0,
    maxHeight: '640px',
    overflowY: 'auto',
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#FFFFFF',
    paddingBottom: tokens.spacingVerticalS,
  },
  roleSidebarSearch: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: tokens.spacingVerticalS,
    backgroundColor: '#FFFFFF',
    borderBottom: `1px solid ${veracityColors.firstLight}`,
  },
  roleSidebarGroupHeader: {
    padding: `6px ${tokens.spacingHorizontalM}`,
    // gentleHaze, one step darker than firstLight: firstLight is the row
    // hover color, so a firstLight header read as a hovered row.
    backgroundColor: veracityColors.gentleHaze,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: veracityColors.duskBlue,
  },
  roleSidebarItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    padding: `6px ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    ':hover': { backgroundColor: veracityColors.firstLight },
  },
  roleSidebarItemActive: {
    borderLeftColor: veracityColors.veracityOrange,
    backgroundColor: veracityColors.firstLight,
  },
  roleSidebarItemName: {
    color: veracityColors.midnightCharcoal,
    fontSize: tokens.fontSizeBase300,
  },
  roleSidebarItemNameActive: {
    fontWeight: tokens.fontWeightSemibold,
  },
  roleSidebarCount: {
    color: veracityColors.duskBlue,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  rolePane: {
    flexGrow: 1,
    minWidth: 0,
  },
  rolePaneHeader: {
    marginBottom: tokens.spacingVerticalM,
  },
  rolePaneValue: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    marginLeft: tokens.spacingHorizontalS,
  },
  rolePaneCapsTitle: {
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: veracityColors.duskBlue,
    display: 'block',
  },
  rolePaneCaps: {
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rolePaneCapRow: {
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.midnightCharcoal,
  },
  rolePaneCapSurface: {
    color: veracityColors.duskBlue,
    marginLeft: tokens.spacingHorizontalS,
  },
  // Group header inside the By User checkbox list
  roleGridGroupHeader: {
    padding: `6px ${tokens.spacingHorizontalM}`,
    backgroundColor: veracityColors.gentleHaze,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: veracityColors.duskBlue,
    marginTop: tokens.spacingVerticalM,
    display: 'block',
    ':first-child': { marginTop: 0 },
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: tokens.spacingVerticalM,
    color: veracityColors.duskBlue,
  },
  notAdmin: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    color: veracityColors.duskBlue,
  },
});

// =============================================================================
// By-User view
// =============================================================================

function ByUserView({
  tenantId,
  currentAdminOid,
  currentAdminName,
}: {
  tenantId: string;
  currentAdminOid: string;
  currentAdminName: string;
}) {
  const styles = useStyles();
  const qc = useQueryClient();

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<FoundUser[]>([]);
  const [selected, setSelected] = useState<FoundUser | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pending selection has no oid: pull all rows and filter to its email below.
  const assignmentsQuery = useUserRoleAssignments(
    selected?.pending ? undefined : selected?.homeAccountId,
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const users = await searchSystemUsers(query, tenantId);
        setResults(users);
      } catch (err) {
        console.error('User search failed:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, tenantId]);

  const currentRoleValues = useMemo(() => {
    const rows = assignmentsQuery.data ?? [];
    if (selected?.pending) {
      return new Set(
        rows
          .filter((a) => !a.userObjectId && (a.userPrincipalName ?? '').toLowerCase() === selected.email)
          .map((a) => a.roleValue),
      );
    }
    return new Set(rows.map((a) => a.roleValue));
  }, [assignmentsQuery.data, selected]);

  const runMany = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBulkBusy(true);
      setLastError(null);
      try {
        await fn();
        qc.invalidateQueries({ queryKey: userRoleAssignmentKeys.all });
      } catch (err) {
        setLastError(err instanceof Error ? err.message : String(err));
      } finally {
        setBulkBusy(false);
      }
    },
    [qc],
  );

  const handleToggle = useCallback(
    async (roleValue: string, nextChecked: boolean) => {
      if (!selected) return;
      await runMany(async () => {
        if (nextChecked) {
          await grantRoleWithTeamSync(
            {
              // Empty oid writes a pending row; the BFF reconciles it on
              // the user's first sign-in (oid patch + team sync).
              userObjectId: selected.pending ? '' : selected.homeAccountId,
              userPrincipalName: selected.email,
              roleValue,
              grantedBy: currentAdminOid,
              grantedByName: currentAdminName,
            },
            selected.pending ? null : selected.systemuserid,
          );
        } else if (selected.pending) {
          await userRoleAssignmentApi.revokePendingByEmailAndRole(selected.email, roleValue);
        } else {
          await revokeRoleWithTeamSync(
            selected.homeAccountId,
            roleValue,
            selected.systemuserid,
          );
        }
      });
    },
    [selected, currentAdminOid, currentAdminName, runMany],
  );

  const handleGrantAll = useCallback(async () => {
    if (!selected) return;
    await runMany(async () => {
      await Promise.all(
        VERA_APP_ROLES.filter((r) => !currentRoleValues.has(r.value)).map((r) =>
          grantRoleWithTeamSync(
            {
              userObjectId: selected.pending ? '' : selected.homeAccountId,
              userPrincipalName: selected.email,
              roleValue: r.value,
              grantedBy: currentAdminOid,
              grantedByName: currentAdminName,
            },
            selected.pending ? null : selected.systemuserid,
          ),
        ),
      );
    });
  }, [selected, currentRoleValues, currentAdminOid, currentAdminName, runMany]);

  const handleRevokeAll = useCallback(async () => {
    if (!selected) return;
    await runMany(async () => {
      await Promise.all(
        Array.from(currentRoleValues).map((roleValue) =>
          selected.pending
            ? userRoleAssignmentApi.revokePendingByEmailAndRole(selected.email, roleValue)
            : revokeRoleWithTeamSync(selected.homeAccountId, roleValue, selected.systemuserid),
        ),
      );
    });
  }, [selected, currentRoleValues, runMany]);

  return (
    <>
      <Input
        className={styles.searchBox}
        placeholder="Search by name or email (min 2 chars)…"
        contentBefore={<SearchRegular />}
        value={query}
        onChange={(_, data) => setQuery(data.value)}
      />

      {searching && <Spinner size="tiny" label="Searching…" />}

      {!searching && results.length > 0 && (
        <div className={styles.resultsList}>
          {results.map((u) => {
            const active = selected?.homeAccountId === u.homeAccountId;
            return (
              <div
                key={u.homeAccountId}
                className={active ? styles.resultRowActive : styles.resultRow}
                onClick={() => setSelected(u)}
              >
                <div className={styles.resultName}>{u.name}</div>
                <div className={styles.resultEmail}>{u.email}</div>
              </div>
            );
          })}
        </div>
      )}

      {!selected && !searching && query.length >= 2 && results.length === 0 && (
        <div style={{ marginBottom: tokens.spacingVerticalM }}>
          <Body1 style={{ color: veracityColors.duskBlue }}>No active user found matching "{query}".</Body1>
          {looksLikeEmail(query) ? (
            <div style={{ marginTop: tokens.spacingVerticalS }}>
              <Button icon={<PersonAddRegular />} onClick={() => setSelected(pendingFoundUser(query))}>
                Pre-provision {query.trim().toLowerCase()}
              </Button>
              <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginTop: tokens.spacingVerticalXS }}>
                Grant roles now; they activate automatically the first time this person signs in to Vera. No Dynamics visit needed first.
              </Caption1>
            </div>
          ) : (
            <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginTop: tokens.spacingVerticalXS }}>
              If they haven't signed in to Vera yet, enter their full email address to pre-provision their access.
            </Caption1>
          )}
        </div>
      )}

      {selected && (
        <Card className={styles.selectedPanel}>
          <div className={styles.selectedHeader}>
            <div>
              <Subtitle2>{selected.name}</Subtitle2>
              <div className={styles.resultEmail}>
                {selected.pending
                  ? 'Has not signed in to Vera yet. Roles below activate on their first sign-in.'
                  : selected.email}
              </div>
            </div>
            {selected.pending && (
              <Badge appearance="tint" color="warning">
                Pending first sign-in
              </Badge>
            )}
            {(assignmentsQuery.isLoading || bulkBusy) && <Spinner size="tiny" />}
          </div>

          <Divider style={{ marginBottom: tokens.spacingVerticalM }} />

          <div className={styles.bulkActions}>
            <Button
              icon={<CheckmarkSquareRegular />}
              disabled={bulkBusy || currentRoleValues.size === VERA_APP_ROLES.length}
              onClick={handleGrantAll}
            >
              Grant all
            </Button>
            <Button
              icon={<DismissSquareRegular />}
              disabled={bulkBusy || currentRoleValues.size === 0}
              onClick={handleRevokeAll}
            >
              Revoke all
            </Button>
            <Caption1 style={{ marginLeft: 'auto', color: veracityColors.duskBlue, alignSelf: 'center' }}>
              {currentRoleValues.size} of {VERA_APP_ROLES.length} granted
            </Caption1>
          </div>

          {lastError && (
            <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
              <MessageBarBody>{lastError}</MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.roleGrid}>
            {groupedVeraRoles().map(({ group, roles: groupRoles }) => (
              <Fragment key={group}>
                <Caption1 className={styles.roleGridGroupHeader}>{group}</Caption1>
                {groupRoles.map((role) => {
                  const checked = currentRoleValues.has(role.value);
                  return (
                    <div key={role.value} className={styles.roleRow}>
                      <Checkbox
                        checked={checked}
                        disabled={bulkBusy || assignmentsQuery.isLoading}
                        onChange={(_, data) => handleToggle(role.value, !!data.checked)}
                      />
                      <div className={styles.roleText}>
                        <span className={styles.roleValue}>{role.value}</span>
                        <Body1>{role.displayName}</Body1>
                        <Caption1 className={styles.roleDescription}>{role.description}</Caption1>
                      </div>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

// =============================================================================
// By-Role view
// =============================================================================

function ByRoleView({
  tenantId,
  currentAdminOid,
  currentAdminName,
}: {
  tenantId: string;
  currentAdminOid: string;
  currentAdminName: string;
}) {
  const styles = useStyles();
  const qc = useQueryClient();

  const [roleValue, setRoleValue] = useState<string>('');
  const [query, setQuery] = useState('');
  const [roleQuery, setRoleQuery] = useState('');
  // Holder filter: everyone / only users who hold the role / only users who don't.
  const [holderFilter, setHolderFilter] = useState<'all' | 'has' | 'lacks'>('all');
  const [lastError, setLastError] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['admin', 'activeSystemUsers', tenantId],
    queryFn: () => fetchAllActiveSystemUsers(tenantId),
    staleTime: 5 * 60 * 1000,
  });

  // All assignments, unfiltered — used to compute which users have the role
  const assignmentsQuery = useUserRoleAssignments();

  const roleHolders = useMemo(() => {
    if (!roleValue) return new Set<string>();
    return new Set(
      (assignmentsQuery.data ?? [])
        .filter((a) => a.roleValue === roleValue)
        .map((a) => a.userObjectId),
    );
  }, [assignmentsQuery.data, roleValue]);

  const visibleUsers = useMemo(() => {
    let users = usersQuery.data ?? [];
    if (holderFilter === 'has') users = users.filter((u) => roleHolders.has(u.homeAccountId));
    if (holderFilter === 'lacks') users = users.filter((u) => !roleHolders.has(u.homeAccountId));
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [usersQuery.data, query, holderFilter, roleHolders]);

  // Counts for the filter labels — across all active users, before the search box narrows.
  const holderCount = useMemo(
    () => (usersQuery.data ?? []).filter((u) => roleHolders.has(u.homeAccountId)).length,
    [usersQuery.data, roleHolders],
  );
  const nonHolderCount = (usersQuery.data?.length ?? 0) - holderCount;

  const runMany = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBulkBusy(true);
      setLastError(null);
      try {
        await fn();
        qc.invalidateQueries({ queryKey: userRoleAssignmentKeys.all });
      } catch (err) {
        setLastError(err instanceof Error ? err.message : String(err));
      } finally {
        setBulkBusy(false);
      }
    },
    [qc],
  );

  const handleToggle = useCallback(
    async (user: FoundUser, nextChecked: boolean) => {
      if (!roleValue) return;
      await runMany(async () => {
        if (nextChecked) {
          await grantRoleWithTeamSync(
            {
              userObjectId: user.homeAccountId,
              userPrincipalName: user.email,
              roleValue,
              grantedBy: currentAdminOid,
              grantedByName: currentAdminName,
            },
            user.systemuserid,
          );
        } else {
          await revokeRoleWithTeamSync(user.homeAccountId, roleValue, user.systemuserid);
        }
      });
    },
    [roleValue, currentAdminOid, currentAdminName, runMany],
  );

  const visibleWithoutRole = useMemo(
    () => visibleUsers.filter((u) => !roleHolders.has(u.homeAccountId)),
    [visibleUsers, roleHolders],
  );
  const visibleWithRole = useMemo(
    () => visibleUsers.filter((u) => roleHolders.has(u.homeAccountId)),
    [visibleUsers, roleHolders],
  );

  const handleGrantVisible = useCallback(async () => {
    if (!roleValue || visibleWithoutRole.length === 0) return;
    await runMany(async () => {
      await Promise.all(
        visibleWithoutRole.map((u) =>
          grantRoleWithTeamSync(
            {
              userObjectId: u.homeAccountId,
              userPrincipalName: u.email,
              roleValue,
              grantedBy: currentAdminOid,
              grantedByName: currentAdminName,
            },
            u.systemuserid,
          ),
        ),
      );
    });
  }, [roleValue, visibleWithoutRole, currentAdminOid, currentAdminName, runMany]);

  const handleRevokeVisible = useCallback(async () => {
    if (!roleValue || visibleWithRole.length === 0) return;
    await runMany(async () => {
      await Promise.all(
        visibleWithRole.map((u) =>
          revokeRoleWithTeamSync(u.homeAccountId, roleValue, u.systemuserid),
        ),
      );
    });
  }, [roleValue, visibleWithRole, runMany]);

  const selectedRole = VERA_APP_ROLES.find((r) => r.value === roleValue);

  // Sidebar role search: matches display name, role value, or description.
  const visibleRoleGroups = useMemo(() => {
    const groups = groupedVeraRoles();
    const q = roleQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(({ group, roles: groupRoles }) => ({
        group,
        roles: groupRoles.filter(
          (r) =>
            r.displayName.toLowerCase().includes(q) ||
            r.value.toLowerCase().includes(q) ||
            r.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.roles.length > 0);
  }, [roleQuery]);

  // Holder count per role across ALL assignments; drives the sidebar badges.
  const holderCountByRole = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of assignmentsQuery.data ?? []) {
      counts.set(a.roleValue, (counts.get(a.roleValue) ?? 0) + 1);
    }
    return counts;
  }, [assignmentsQuery.data]);

  return (
    <div className={styles.byRoleLayout}>
      <div className={styles.roleSidebar}>
        <div className={styles.roleSidebarSearch}>
          <Input
            size="small"
            style={{ width: '100%' }}
            placeholder="Search roles…"
            contentBefore={<SearchRegular />}
            value={roleQuery}
            onChange={(_, data) => setRoleQuery(data.value)}
          />
        </div>
        {visibleRoleGroups.length === 0 && (
          <Caption1 style={{ display: 'block', padding: tokens.spacingVerticalM, color: veracityColors.duskBlue }}>
            No roles match "{roleQuery.trim()}".
          </Caption1>
        )}
        {visibleRoleGroups.map(({ group, roles: groupRoles }) => (
          <Fragment key={group}>
            <div className={styles.roleSidebarGroupHeader}>{group}</div>
            {groupRoles.map((r) => {
              const active = r.value === roleValue;
              return (
                <div
                  key={r.value}
                  className={active
                    ? mergeClasses(styles.roleSidebarItem, styles.roleSidebarItemActive)
                    : styles.roleSidebarItem}
                  onClick={() => setRoleValue(r.value)}
                >
                  <span
                    className={active
                      ? mergeClasses(styles.roleSidebarItemName, styles.roleSidebarItemNameActive)
                      : styles.roleSidebarItemName}
                  >
                    {r.displayName}
                  </span>
                  <span className={styles.roleSidebarCount}>{holderCountByRole.get(r.value) ?? 0}</span>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>

      <div className={styles.rolePane}>
        {!selectedRole && (
          <div className={styles.pickRolePrompt}>
            <Body1>Pick a role on the left to see who holds it.</Body1>
          </div>
        )}

        {selectedRole && (
        <>
          <div className={styles.rolePaneHeader}>
            <Subtitle2>
              {selectedRole.displayName}
              <span className={styles.rolePaneValue}>{selectedRole.value}</span>
            </Subtitle2>
            <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginTop: '2px' }}>
              {selectedRole.description}
            </Caption1>
            {/* Derived from the capability registry — the same objects the
                gates execute — so this list cannot drift from behavior. */}
            <span className={styles.rolePaneCapsTitle}>Unlocks</span>
            <div className={styles.rolePaneCaps}>
              {selectedRole.value === GLOBAL_ADMIN_ROLE && (
                <div className={styles.rolePaneCapRow}>Every Vera module, plus everything below</div>
              )}
              {Object.values(ROLE_PREFIX).includes(selectedRole.value.split('.')[0] ?? '') && (
                <div className={styles.rolePaneCapRow}>Module access: {selectedRole.group}</div>
              )}
              {capabilitiesForRole(selectedRole.value).map((cap) => (
                <div key={cap.id} className={styles.rolePaneCapRow}>
                  {cap.label}
                  <span className={styles.rolePaneCapSurface}>{cap.surface}</span>
                </div>
              ))}
            </div>
          </div>

          <Input
            className={styles.searchBox}
            placeholder="Filter users by name or email…"
            contentBefore={<SearchRegular />}
            value={query}
            onChange={(_, data) => setQuery(data.value)}
          />

          <TabList
            size="small"
            selectedValue={holderFilter}
            onTabSelect={(_: SelectTabEvent, data: SelectTabData) =>
              setHolderFilter(data.value as 'all' | 'has' | 'lacks')
            }
            style={{ marginBottom: tokens.spacingVerticalM }}
          >
            <Tab value="all">All users</Tab>
            <Tab value="has">Has this role ({holderCount})</Tab>
            <Tab value="lacks">Doesn&apos;t have it ({nonHolderCount})</Tab>
          </TabList>

          <div className={styles.bulkActions}>
            <Button
              icon={<CheckmarkSquareRegular />}
              disabled={bulkBusy || visibleWithoutRole.length === 0}
              onClick={handleGrantVisible}
            >
              Grant to {visibleWithoutRole.length} visible
            </Button>
            <Button
              icon={<DismissSquareRegular />}
              disabled={bulkBusy || visibleWithRole.length === 0}
              onClick={handleRevokeVisible}
            >
              Revoke from {visibleWithRole.length} visible
            </Button>
            <Caption1 style={{ marginLeft: 'auto', color: veracityColors.duskBlue, alignSelf: 'center' }}>
              {roleHolders.size} user{roleHolders.size === 1 ? '' : 's'} currently hold this role
            </Caption1>
            {(bulkBusy || usersQuery.isLoading || assignmentsQuery.isLoading) && <Spinner size="tiny" />}
          </div>

          {lastError && (
            <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
              <MessageBarBody>{lastError}</MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.userTable}>
            {visibleUsers.length === 0 && (
              <div style={{ padding: tokens.spacingVerticalL, textAlign: 'center', color: veracityColors.duskBlue }}>
                {query
                  ? `No users match "${query}".`
                  : holderFilter === 'has'
                    ? 'No one holds this role yet.'
                    : holderFilter === 'lacks'
                      ? 'Everyone already holds this role.'
                      : 'No active users.'}
              </div>
            )}
            {visibleUsers.map((u) => {
              const checked = roleHolders.has(u.homeAccountId);
              return (
                <div key={u.homeAccountId} className={styles.userRow}>
                  <Checkbox
                    checked={checked}
                    disabled={bulkBusy || assignmentsQuery.isLoading}
                    onChange={(_, data) => handleToggle(u, !!data.checked)}
                  />
                  <div className={styles.userText}>
                    <div className={styles.resultName}>{u.name}</div>
                    <div className={styles.resultEmail}>{u.email}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.statusBar}>
            <Caption1>
              Showing {visibleUsers.length} of {usersQuery.data?.length ?? 0} active users
              {query && ` (filtered by "${query}")`}
            </Caption1>
          </div>
        </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Bulk-Paste view
// =============================================================================

const EMAIL_REGEX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

function summarizeGrant(role: string, realCount: number, pendingCount: number): string {
  const parts: string[] = [];
  if (realCount > 0) parts.push(`granted ${role} to ${realCount} user${realCount === 1 ? '' : 's'}`);
  if (pendingCount > 0)
    parts.push(`pre-provisioned ${pendingCount} email${pendingCount === 1 ? '' : 's'} (active on first sign-in)`);
  if (parts.length === 0) return '';
  return parts.join(', ').replace(/^./, (c) => c.toUpperCase()) + '.';
}

function summarizeRevoke(role: string, realCount: number, pendingCount: number): string {
  const parts: string[] = [];
  if (realCount > 0) parts.push(`revoked ${role} from ${realCount} user${realCount === 1 ? '' : 's'}`);
  if (pendingCount > 0)
    parts.push(`removed ${pendingCount} pending grant${pendingCount === 1 ? '' : 's'}`);
  if (parts.length === 0) return '';
  return parts.join(', ').replace(/^./, (c) => c.toUpperCase()) + '.';
}

function extractEmails(raw: string): string[] {
  const matches = raw.match(EMAIL_REGEX) ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of matches) {
    const lower = m.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      out.push(lower);
    }
  }
  return out;
}

interface PastePreview {
  /** Emails that match a Dataverse systemuser (the user has signed in at least once). */
  matched: FoundUser[];
  /** Matched users who already hold the role. */
  alreadyHave: FoundUser[];
  /** Matched users who don't yet hold the role — Grant will assign + team-sync. */
  willGrant: FoundUser[];
  /**
   * Emails with no Dataverse systemuser yet (pre-provisioning targets).
   * Grant will write a pending row keyed by email; the BFF reconciles on
   * the user's first sign-in.
   */
  pendingEmails: string[];
  /** Pending emails that already have a pending grant for this role. */
  pendingAlreadyHave: string[];
  /** Pending emails without a pending grant yet — Grant will pre-provision. */
  willPreProvision: string[];
}

function ByPasteView({
  tenantId,
  currentAdminOid,
  currentAdminName,
}: {
  tenantId: string;
  currentAdminOid: string;
  currentAdminName: string;
}) {
  const styles = useStyles();
  const qc = useQueryClient();

  const [roleValue, setRoleValue] = useState<string>('');
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState<PastePreview | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastSuccess, setLastSuccess] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bumped by handleGrant/handleRevoke after their invalidateQueries resolves;
  // an effect below re-runs handleParse on the next render once React has
  // re-derived `roleHolders` from the freshly invalidated assignments cache.
  // Replaces a brittle `setTimeout(handleParse, 100)` race.
  const [reparseSeq, setReparseSeq] = useState(0);

  const usersQuery = useQuery({
    queryKey: ['admin', 'activeSystemUsers', tenantId],
    queryFn: () => fetchAllActiveSystemUsers(tenantId),
    staleTime: 5 * 60 * 1000,
  });

  const assignmentsQuery = useUserRoleAssignments();

  // Real holders: users with a systemuser AND an assignment (oid populated)
  const roleHolders = useMemo(() => {
    if (!roleValue) return new Set<string>();
    return new Set(
      (assignmentsQuery.data ?? [])
        .filter((a) => a.roleValue === roleValue && a.userObjectId)
        .map((a) => a.userObjectId),
    );
  }, [assignmentsQuery.data, roleValue]);

  // Pending holders: emails with a pre-provisioned grant awaiting first sign-in
  const pendingHolderEmails = useMemo(() => {
    if (!roleValue) return new Set<string>();
    return new Set(
      (assignmentsQuery.data ?? [])
        .filter((a) => a.roleValue === roleValue && !a.userObjectId && a.userPrincipalName)
        .map((a) => a.userPrincipalName!.toLowerCase()),
    );
  }, [assignmentsQuery.data, roleValue]);

  const handleParse = useCallback(() => {
    setLastError(null);
    setLastSuccess(null);
    if (!roleValue) {
      setLastError('Pick a role first.');
      return;
    }
    const users = usersQuery.data ?? [];
    const byEmail = new Map<string, FoundUser>();
    for (const u of users) {
      if (u.email) byEmail.set(u.email.toLowerCase(), u);
    }
    const emails = extractEmails(pasteText);
    if (emails.length === 0) {
      setLastError('No email addresses found in pasted text.');
      setPreview(null);
      return;
    }
    const matched: FoundUser[] = [];
    const pendingEmails: string[] = [];
    for (const e of emails) {
      const u = byEmail.get(e);
      if (u) matched.push(u);
      else pendingEmails.push(e);
    }
    const alreadyHave = matched.filter((u) => roleHolders.has(u.homeAccountId));
    const willGrant = matched.filter((u) => !roleHolders.has(u.homeAccountId));
    const pendingAlreadyHave = pendingEmails.filter((e) => pendingHolderEmails.has(e));
    const willPreProvision = pendingEmails.filter((e) => !pendingHolderEmails.has(e));
    setPreview({
      matched,
      alreadyHave,
      willGrant,
      pendingEmails,
      pendingAlreadyHave,
      willPreProvision,
    });
  }, [pasteText, roleValue, usersQuery.data, roleHolders, pendingHolderEmails]);

  const runMany = useCallback(
    async (fn: () => Promise<unknown>, successMsg: string) => {
      setBulkBusy(true);
      setLastError(null);
      setLastSuccess(null);
      try {
        await fn();
        await qc.invalidateQueries({ queryKey: userRoleAssignmentKeys.all });
        setLastSuccess(successMsg);
      } catch (err) {
        setLastError(err instanceof Error ? err.message : String(err));
      } finally {
        setBulkBusy(false);
      }
    },
    [qc],
  );

  const handleGrant = useCallback(async () => {
    if (!roleValue || !preview) return;
    const realTargets = preview.willGrant;
    const pendingTargets = preview.willPreProvision;
    const totalCount = realTargets.length + pendingTargets.length;
    if (totalCount === 0) return;

    await runMany(async () => {
      await Promise.all([
        ...realTargets.map((u) =>
          grantRoleWithTeamSync(
            {
              userObjectId: u.homeAccountId,
              userPrincipalName: u.email,
              roleValue,
              grantedBy: currentAdminOid,
              grantedByName: currentAdminName,
            },
            u.systemuserid,
          ),
        ),
        // Pending grants: empty oid, no systemuser to sync. The BFF
        // patches the oid + adds the user to the team on first sign-in.
        ...pendingTargets.map((email) =>
          grantRoleWithTeamSync(
            {
              userObjectId: '',
              userPrincipalName: email,
              roleValue,
              grantedBy: currentAdminOid,
              grantedByName: currentAdminName,
            },
            null,
          ),
        ),
      ]);
    }, summarizeGrant(roleValue, realTargets.length, pendingTargets.length));
    setReparseSeq((n) => n + 1);
  }, [roleValue, preview, runMany, currentAdminOid, currentAdminName]);

  const handleRevoke = useCallback(async () => {
    if (!roleValue || !preview) return;
    const realTargets = preview.alreadyHave;
    const pendingTargets = preview.pendingAlreadyHave;
    const totalCount = realTargets.length + pendingTargets.length;
    if (totalCount === 0) return;

    await runMany(async () => {
      await Promise.all([
        ...realTargets.map((u) =>
          revokeRoleWithTeamSync(u.homeAccountId, roleValue, u.systemuserid),
        ),
        // Pending revokes: just remove the row. No team membership to undo.
        ...pendingTargets.map((email) =>
          userRoleAssignmentApi.revokePendingByEmailAndRole(email, roleValue),
        ),
      ]);
    }, summarizeRevoke(roleValue, realTargets.length, pendingTargets.length));
    setReparseSeq((n) => n + 1);
  }, [roleValue, preview, runMany]);

  // Re-parse after grant/revoke once React has re-rendered with the updated
  // assignments cache (so `roleHolders` reflects the new state). The effect
  // ignores the initial mount via the seq > 0 guard.
  useEffect(() => {
    if (reparseSeq === 0) return;
    handleParse();
  }, [reparseSeq, handleParse]);

  const selectedRole = VERA_APP_ROLES.find((r) => r.value === roleValue);
  const usersLoading = usersQuery.isLoading || assignmentsQuery.isLoading;

  return (
    <>
      <div style={{ marginBottom: tokens.spacingVerticalM, maxWidth: '480px' }}>
        <Combobox
          placeholder="Pick a role to manage…"
          value={selectedRole ? `${selectedRole.displayName} (${selectedRole.value})` : ''}
          onOptionSelect={(_, data) => {
            setRoleValue(data.optionValue ?? '');
            setPreview(null);
            setLastSuccess(null);
          }}
        >
          {groupedVeraRoles().map(({ group, roles: groupRoles }) => (
            <OptionGroup key={group} label={group}>
              {groupRoles.map((r) => (
                <Option key={r.value} value={r.value} text={`${r.displayName} (${r.value})`}>
                  <span style={{ fontWeight: tokens.fontWeightSemibold }}>
                    {r.displayName}
                    <span style={{ fontFamily: 'monospace', marginLeft: 8, color: veracityColors.duskBlue, fontWeight: tokens.fontWeightRegular }}>
                      {r.value}
                    </span>
                  </span>
                </Option>
              ))}
            </OptionGroup>
          ))}
        </Combobox>
        {selectedRole && (
          <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginTop: tokens.spacingVerticalXS }}>
            {selectedRole.description}
          </Caption1>
        )}
      </div>

      <div style={{ marginBottom: tokens.spacingVerticalS, maxWidth: '720px' }}>
        <Body1 style={{ marginBottom: tokens.spacingVerticalXS, display: 'block' }}>
          Paste a list of users
        </Body1>
        <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginBottom: tokens.spacingVerticalS }}>
          Accepts Outlook format (<code>"Name" &lt;email&gt;;</code>), comma-separated, newline-separated, or any mixed text — only email addresses are extracted.
        </Caption1>
        <Textarea
          style={{ width: '100%' }}
          rows={6}
          placeholder={'"Adam Stichter" <astichter@oneveracity.com>; "Alex White" <awhite@oneveracity.com>; …'}
          value={pasteText}
          onChange={(_, data) => {
            setPasteText(data.value);
            setPreview(null);
            setLastSuccess(null);
          }}
          disabled={bulkBusy}
        />
      </div>

      <div className={styles.bulkActions}>
        <Button
          icon={<ClipboardPasteRegular />}
          appearance="primary"
          disabled={bulkBusy || usersLoading || !roleValue || pasteText.trim().length === 0}
          onClick={handleParse}
        >
          Parse &amp; preview
        </Button>
        {usersLoading && <Spinner size="tiny" label="Loading users…" />}
        {bulkBusy && <Spinner size="tiny" label="Applying…" />}
      </div>

      {lastError && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>{lastError}</MessageBarBody>
        </MessageBar>
      )}

      {lastSuccess && (
        <MessageBar intent="success" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>{lastSuccess}</MessageBarBody>
        </MessageBar>
      )}

      {preview && (() => {
        const grantTotal = preview.willGrant.length + preview.willPreProvision.length;
        const revokeTotal = preview.alreadyHave.length + preview.pendingAlreadyHave.length;
        return (
          <Card className={styles.selectedPanel}>
            <Subtitle2 style={{ marginBottom: tokens.spacingVerticalS }}>Preview</Subtitle2>
            <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginBottom: tokens.spacingVerticalM }}>
              {preview.matched.length} matched · {preview.pendingEmails.length} pending first sign-in · {revokeTotal} already have <code>{roleValue}</code>
            </Caption1>

            <Divider style={{ marginBottom: tokens.spacingVerticalM }} />

            <div className={styles.bulkActions}>
              <Button
                icon={<CheckmarkSquareRegular />}
                appearance="primary"
                disabled={bulkBusy || grantTotal === 0}
                onClick={handleGrant}
              >
                Grant {roleValue} to {grantTotal}
                {preview.willPreProvision.length > 0 &&
                  ` (incl. ${preview.willPreProvision.length} pre-provisioned)`}
              </Button>
              <Button
                icon={<DismissSquareRegular />}
                disabled={bulkBusy || revokeTotal === 0}
                onClick={handleRevoke}
              >
                Revoke {roleValue} from {revokeTotal}
              </Button>
            </div>

            {preview.matched.length > 0 && (
              <>
                <Body1 style={{ marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalS }}>
                  Matched ({preview.matched.length})
                </Body1>
                <div className={styles.userTable}>
                  {preview.matched.map((u) => {
                    const has = roleHolders.has(u.homeAccountId);
                    return (
                      <div key={u.homeAccountId} className={styles.userRow}>
                        <div style={{ width: '24px', textAlign: 'center' }}>
                          {has ? (
                            <CheckmarkSquareRegular style={{ color: veracityColors.veracityOrange }} />
                          ) : (
                            <DismissSquareRegular style={{ color: veracityColors.duskBlue }} />
                          )}
                        </div>
                        <div className={styles.userText}>
                          <div className={styles.resultName}>{u.name}</div>
                          <div className={styles.resultEmail}>{u.email}</div>
                        </div>
                        <Caption1 style={{ color: has ? veracityColors.veracityOrange : veracityColors.duskBlue }}>
                          {has ? 'has role' : 'will grant'}
                        </Caption1>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {preview.pendingEmails.length > 0 && (
              <>
                <Body1 style={{ marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalS }}>
                  Pending first sign-in ({preview.pendingEmails.length})
                </Body1>
                <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginBottom: tokens.spacingVerticalS }}>
                  These users haven't signed in to Vera yet. Granting will pre-provision the role — it activates automatically when they first sign in.
                </Caption1>
                <div className={styles.userTable}>
                  {preview.pendingEmails.map((email) => {
                    const queued = pendingHolderEmails.has(email);
                    return (
                      <div key={email} className={styles.userRow}>
                        <div style={{ width: '24px', textAlign: 'center' }}>
                          {queued ? (
                            <CheckmarkSquareRegular style={{ color: veracityColors.veracityOrange }} />
                          ) : (
                            <DismissSquareRegular style={{ color: veracityColors.duskBlue }} />
                          )}
                        </div>
                        <div className={styles.userText}>
                          <div className={styles.resultEmail}>{email}</div>
                        </div>
                        <Caption1 style={{ color: queued ? veracityColors.veracityOrange : veracityColors.duskBlue }}>
                          {queued ? 'pre-provisioned' : 'will pre-provision'}
                        </Caption1>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        );
      })()}
    </>
  );
}

// =============================================================================
// Top-level page
// =============================================================================

export function AdminPage() {
  const styles = useStyles();
  const { user, roles } = useAuth();
  const isAdmin = hasGlobalAdmin(roles);
  const [tab, setTab] = useState<'user' | 'role' | 'paste' | 'health'>('user');

  // Derive the tenant suffix from the signed-in admin's own session oid
  // (BFF format: `<localAccountId>.<homeTenantId>`). This keeps writes in
  // lockstep with what the BFF will look up at sign-in time, regardless of
  // whether VITE_AZURE_TENANT_ID was set at build time. Falling back to
  // env.azure.tenantId for paranoia, but in practice user.oid is the truth.
  // History: prod build once shipped without the env var, producing grants
  // stored as `<oid>.` (empty suffix) that never matched the BFF lookup.
  const tenantId =
    (user?.oid?.includes('.') ? user.oid.split('.').slice(1).join('.') : '') ||
    env.azure.tenantId ||
    '';

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <div className={styles.notAdmin}>
          <ShieldKeyholeRegular style={{ fontSize: '48px', marginBottom: '16px' }} />
          <Subtitle2>Admin access required</Subtitle2>
          <Body1>This page is restricted to users with the Vera Admin role.</Body1>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (!tenantId) {
    return (
      <div className={styles.page}>
        <div className={styles.notAdmin}>
          <ShieldKeyholeRegular style={{ fontSize: '48px', marginBottom: '16px' }} />
          <Subtitle2>Configuration error</Subtitle2>
          <Body1>
            Could not derive the tenant suffix from your session. Grants are
            disabled to prevent writing rows the BFF can&apos;t resolve. Sign
            out and back in; if this persists, contact a Vera developer.
          </Body1>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <ShieldKeyholeRegular className={styles.titleIcon} />
        <Title2>Vera Admin — Role Assignments</Title2>
      </div>
      <Body1 className={styles.subtitle}>
        Grant or revoke per-module roles. Changes apply within 15 minutes for signed-in users, or at next sign-in.
      </Body1>

      <TabList
        className={styles.tabList}
        selectedValue={tab}
        onTabSelect={(_: SelectTabEvent, data: SelectTabData) => setTab(data.value as 'user' | 'role' | 'paste' | 'health')}
      >
        <Tab value="user">By User</Tab>
        <Tab value="role">By Role</Tab>
        <Tab value="paste">Bulk Paste</Tab>
        <Tab value="health">Access Health</Tab>
      </TabList>

      {tab === 'user' && (
        <ByUserView
          tenantId={tenantId}
          currentAdminOid={user.oid}
          currentAdminName={user.name}
        />
      )}
      {tab === 'role' && (
        <ByRoleView
          tenantId={tenantId}
          currentAdminOid={user.oid}
          currentAdminName={user.name}
        />
      )}
      {tab === 'paste' && (
        <ByPasteView
          tenantId={tenantId}
          currentAdminOid={user.oid}
          currentAdminName={user.name}
        />
      )}
      {tab === 'health' && <AccessHealthView />}
    </div>
  );
}
